import type { RefObject } from "react";
import { Container } from "@cw/ui";
import { aiToolLabel } from "@cw/forms/ai-tools";
import type { Environment } from "./intake-options";

type GetStartedReceiptProps = {
  receipt: { id: string; confirmationSent: boolean };
  environment: Environment;
  bundleName: string;
  headingRef: RefObject<HTMLHeadingElement | null>;
};

export function GetStartedReceipt({ receipt, environment, bundleName, headingRef }: GetStartedReceiptProps) {
  const details: [string, string][] = [
    ["Organization", environment.company],
    ["Edge bundle", bundleName],
    ["Total users", Number(environment.totalUsers).toLocaleString("en-US")],
    ["Total sites", Number(environment.totalSites).toLocaleString("en-US")],
    ["Current platform", environment.currentPlatform.trim() || "Not specified"],
    ["AI tools in use", environment.aiTools.length ? environment.aiTools.map(aiToolLabel).join(", ") : "Not specified"],
    ["Primary contact", environment.name.trim()],
  ];
  return (
    <section id="get-started-form" className="scroll-mt-24 bg-paper pb-20 pt-10">
      <Container>
        <article className="overflow-hidden rounded-[10px] border border-hairline bg-paper-raised" aria-labelledby="edge-receipt-heading">
          <header className="px-6 py-8 sm:px-10 sm:py-9">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.14em] text-ink">
                <span aria-hidden="true" className="flex size-7 items-center justify-center rounded-full bg-accent-soft text-ink"><svg viewBox="0 0 16 16" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2.2"><path strokeLinecap="round" strokeLinejoin="round" d="m3 8.5 3 3 7-7" /></svg></span>
                Request received
              </p>
              <p className="text-xs text-slate">Reference <span className="ml-2 break-all font-mono text-ink">{receipt.id}</span></p>
            </div>
            <h2 id="edge-receipt-heading" ref={headingRef} tabIndex={-1} className="mt-6 scroll-mt-28 font-display text-2xl font-semibold tracking-tight text-ink outline-none sm:text-3xl">We’ve received your configuration</h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-slate">Thank you, {environment.name.trim()}. Your environment details are with the PSCX Edge team.</p>
          </header>
          <div className="grid border-t border-hairline lg:grid-cols-[minmax(0,1fr)_23rem]">
            <section className="px-6 py-8 sm:px-10" aria-labelledby="edge-request-details">
              <h3 id="edge-request-details" className="text-xs font-semibold uppercase tracking-[0.14em] text-slate">Your configuration at a glance</h3>
              <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-7">
                {details.map(([label, value]) => <div key={label} className="min-w-0"><dt className="text-xs text-slate">{label}</dt><dd className="mt-2 break-words text-sm font-semibold leading-6 text-ink">{value}</dd></div>)}
              </dl>
            </section>
            <section className="border-t border-hairline bg-mist px-6 py-8 sm:px-10 lg:border-l lg:border-t-0" aria-labelledby="edge-engineering-review">
              <div aria-hidden="true" className="mb-5 h-0.5 w-8 bg-cta" />
              <h3 id="edge-engineering-review" className="font-display text-xl font-semibold text-ink">Your specialist review</h3>
              <p className="mt-3 text-sm leading-7 text-slate">An Edge specialist will review your environment and contact you to confirm the operating model, answer questions, and shape your onboarding plan.</p>
              <p className="mt-6 text-xs leading-6 text-slate">Need to add something to your request?</p>
              <a href="mailto:edge@pscx.ai" className="mt-1 inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-ink underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink">Contact the Edge team <span aria-hidden="true">→</span></a>
            </section>
          </div>
          <div role="status" className="border-t border-hairline px-6 py-5 text-sm leading-6 text-slate sm:px-10">
            {receipt.confirmationSent
              ? <p>A confirmation with your details has been sent to <span className="break-all font-medium text-ink">{environment.email.trim()}</span>.</p>
              : <p>Your request reached our team, but we couldn’t send your confirmation email. Contact <a className="font-medium text-ink underline underline-offset-4" href="mailto:edge@pscx.ai">edge@pscx.ai</a> and quote reference <span className="break-all font-mono">{receipt.id}</span>.</p>}
          </div>
        </article>
      </Container>
    </section>
  );
}
